"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NumberBaseField = exports.numberFormat = exports.numberThresholdValue = exports.commonNumberFields = void 0;
const lodash_1 = require("lodash");
const field_types_1 = require("../../types/field_types");
const view_types_1 = require("../../types/view_types");
const field_1 = require("./field");
const stat_1 = require("./stat");
const i18n_1 = require("../../exports/i18n");
const utils_1 = require("../../utils");
const joi_1 = __importDefault(require("joi"));
const utils_2 = require("../../model/utils");
exports.commonNumberFields = new Set([
    field_types_1.FieldType.Number,
    field_types_1.FieldType.Currency,
    field_types_1.FieldType.Percent,
    field_types_1.FieldType.Rating,
    field_types_1.FieldType.AutoNumber,
    field_types_1.FieldType.Formula,
]);
// scientific notation threshold
exports.numberThresholdValue = 1e+16;
const numberFormat = (cv, formatting) => {
    const { formatType, precision = 0, symbol, commaStyle } = formatting || {};
    if (formatType === field_types_1.FieldType.Currency) {
        const cellString = (0, utils_1.numberToShow)(cv, precision);
        return (0, utils_1.str2Currency)(cellString, symbol);
    }
    else if (formatType === field_types_1.FieldType.Percent) {
        if (cv == null) {
            return null;
        }
        const cellString = (0, utils_1.numberToShow)((0, utils_1.times)(cv, 100), precision);
        return cellString == null ? null : cellString + '%';
    }
    const cellString = (0, utils_1.numberToShow)(cv, precision);
    return commaStyle ? (0, utils_1.str2Currency)(cellString, '', 3, commaStyle) : cellString;
};
exports.numberFormat = numberFormat;
class NumberBaseField extends field_1.Field {
    constructor(field, state) {
        super(field, state);
        this.field = field;
    }
    get apiMetaProperty() {
        const { defaultValue, precision = 0, commaStyle, symbol } = this.field.property;
        return {
            defaultValue: defaultValue || undefined,
            precision,
            commaStyle: commaStyle || undefined,
            symbol: symbol || undefined
        };
    }
    get openValueJsonSchema() {
        return {
            type: 'number',
            title: this.field.name,
        };
    }
    showFOperatorDesc(type) {
        return NumberBaseField.FOperatorDescMap[type];
    }
    get acceptFilterOperators() {
        return NumberBaseField._acceptFilterOperators;
    }
    get statTypeList() {
        return NumberBaseField._statTypeList;
    }
    get basicValueType() {
        return field_types_1.BasicValueType.Number;
    }
    cellValueToString(_cellValue) {
        return null;
    }
    static _compare(cellValue1, cellValue2) {
        if (cellValue1 === null && cellValue2 === null) {
            return 0;
        }
        if (cellValue1 === null) {
            return -1;
        }
        if (cellValue2 === null) {
            return 1;
        }
        return cellValue1 === cellValue2 ?
            0 : (cellValue1 > cellValue2 ? 1 : -1);
    }
    compare(cellValue1, cellValue2) {
        return NumberBaseField._compare(cellValue1, cellValue2);
    }
    cellValueToStdValue(val) {
        const stdVal = {
            sourceType: this.field.type,
            data: [],
        };
        if (val != null) {
            stdVal.data.push({
                // For fields related to Number type, ensure that the data source accuracy is accurate
                text: this.cellValueToString(val) || '',
                value: val,
            });
        }
        return stdVal;
    }
    // StandardValue -> CellValue needs to undergo data conversion, otherwise it will affect cell copying, filling and other operations
    stdValueToCellValue(stdVal) {
        const { data, sourceType } = stdVal;
        if (data.length === 0) {
            return null;
        }
        const { text, value } = data[0];
        // Convert from Number & Formula related fields
        if (exports.commonNumberFields.has(sourceType) && this.validate(value)) {
            return value;
        }
        // other fields
        const cellValue = (0, utils_1.str2NumericStr)(text);
        return cellValue == null ? null : (0, utils_1.str2number)(cellValue);
    }
    validate(value) {
        return (0, lodash_1.isNumber)(value) && !Number.isNaN(value);
    }
    defaultValueForCondition(condition) {
        if (condition.operator !== view_types_1.FOperator.Is) {
            return null;
        }
        const { value } = condition;
        if (value && value.length && value[0]) {
            return (0, utils_1.str2number)(value[0]);
        }
        return null;
    }
    static _isMeetFilter(operator, cellValue, conditionValue) {
        // TODO: Tidy up the logic here.
        // Field -> NumberBaseField -> [NumberField,RatingField,PercentField,CurrencyField] -> LookupField
        // + getVisibleRows will pre-validate if it is empty or not. Logically speaking, it should not be used, and all filter logic should go here.
        // + When implementing the field isMeetFilter, the unary operator should be judged in advance,
        // and then the polynomial operator should be processed. In this way,
        // the following processing of the comparison value will not affect the normal judgment logic.
        // + Now the field filter also needs to be provided to the single record of automation, and it is determined in advance if it is empty or not.
        if (operator === view_types_1.FOperator.IsEmpty) {
            return cellValue == null;
        }
        if (operator === view_types_1.FOperator.IsNotEmpty) {
            return cellValue != null;
        }
        if (conditionValue == null)
            return true;
        const filterValue = conditionValue[0].trim() === '' ? null : Number(conditionValue[0]);
        if (filterValue == null) {
            return true;
        }
        switch (operator) {
            case view_types_1.FOperator.Is: {
                return !Number.isNaN(filterValue) && cellValue != null && filterValue === cellValue;
            }
            case view_types_1.FOperator.IsNot: {
                return cellValue == null || Number.isNaN(filterValue) || cellValue !== filterValue;
            }
            case view_types_1.FOperator.IsGreater: {
                return cellValue != null && cellValue > filterValue;
            }
            case view_types_1.FOperator.IsGreaterEqual: {
                return cellValue != null && cellValue >= filterValue;
            }
            case view_types_1.FOperator.IsLess: {
                return cellValue != null && cellValue < filterValue;
            }
            case view_types_1.FOperator.IsLessEqual: {
                return cellValue != null && cellValue <= filterValue;
            }
            default: {
                return false;
            }
        }
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        return NumberBaseField._isMeetFilter(operator, cellValue, conditionValue);
    }
    cellValueToApiStandardValue(cellValue) {
        return cellValue;
    }
    cellValueToApiStringValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToOpenValue(cellValue) {
        return cellValue;
    }
    openWriteValueToCellValue(openWriteValue) {
        if ((0, utils_2.isNullValue)(openWriteValue)) {
            return null;
        }
        return openWriteValue;
    }
    validateCellValue(cellValue) {
        return NumberBaseField.cellValueSchema.validate(cellValue);
    }
    validateOpenWriteValue(owv) {
        return NumberBaseField.openWriteValueSchema.validate(owv);
    }
    static _filterValueToOpenFilterValue(value) {
        const num = Number(value === null || value === void 0 ? void 0 : value[0]);
        return isNaN(num) ? null : num;
    }
    filterValueToOpenFilterValue(value) {
        return NumberBaseField._filterValueToOpenFilterValue(value);
    }
    static _openFilterValueToFilterValue(value) {
        return value === null ? null : [value.toString()];
    }
    openFilterValueToFilterValue(value) {
        return NumberBaseField._openFilterValueToFilterValue(value);
    }
    static _validateOpenFilterValue(value) {
        return NumberBaseField.validateOpenFilterSchema.validate(value);
    }
    validateOpenFilterValue(value) {
        return NumberBaseField._validateOpenFilterValue(value);
    }
}
exports.NumberBaseField = NumberBaseField;
NumberBaseField._statTypeList = [
    stat_1.StatType.None,
    stat_1.StatType.CountAll,
    stat_1.StatType.Sum,
    stat_1.StatType.Average,
    stat_1.StatType.Max,
    stat_1.StatType.Min,
    stat_1.StatType.Empty,
    stat_1.StatType.Filled,
    stat_1.StatType.Unique,
    stat_1.StatType.PercentEmpty,
    stat_1.StatType.PercentFilled,
    stat_1.StatType.PercentUnique,
];
NumberBaseField._acceptFilterOperators = [
    view_types_1.FOperator.Is,
    view_types_1.FOperator.IsNot,
    view_types_1.FOperator.IsGreater,
    view_types_1.FOperator.IsGreaterEqual,
    view_types_1.FOperator.IsLess,
    view_types_1.FOperator.IsLessEqual,
    view_types_1.FOperator.IsEmpty,
    view_types_1.FOperator.IsNotEmpty,
    view_types_1.FOperator.IsRepeat,
    view_types_1.FOperator.IsMax,
    view_types_1.FOperator.IsMin
];
NumberBaseField.FOperatorDescMap = {
    [view_types_1.FOperator.Is]: '=',
    [view_types_1.FOperator.IsNot]: '≠',
    [view_types_1.FOperator.IsGreater]: '>',
    [view_types_1.FOperator.IsGreaterEqual]: '≥',
    [view_types_1.FOperator.IsLess]: '<',
    [view_types_1.FOperator.IsLessEqual]: '≤',
    [view_types_1.FOperator.IsEmpty]: (0, i18n_1.t)(i18n_1.Strings.is_empty),
    [view_types_1.FOperator.IsNotEmpty]: (0, i18n_1.t)(i18n_1.Strings.is_not_empty),
    [view_types_1.FOperator.IsRepeat]: (0, i18n_1.t)(i18n_1.Strings.is_repeat),
    [view_types_1.FOperator.IsMax]: (0, i18n_1.t)(i18n_1.Strings.is_max),
    [view_types_1.FOperator.IsMin]: (0, i18n_1.t)(i18n_1.Strings.is_min),
};
NumberBaseField.cellValueSchema = joi_1.default.number().unsafe().allow(null).required();
NumberBaseField.openWriteValueSchema = joi_1.default.number().allow(null).required();
NumberBaseField.validateOpenFilterSchema = joi_1.default.number().allow(null);
//# sourceMappingURL=number_base_field.js.map